import { useEffect, useMemo, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useNavigate } from 'react-router-dom';
import { ErrorPage } from 'components/common/ErrorPage';
import { Layout } from 'components/mui/common/Layout';
import { useLogin } from 'contexts';
import { isMobileApp } from 'utils/common';
import { registerAppNavigation } from 'utils/helios/mapping';
import { getAdminAccessFlags } from 'utils/adminAccess';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { Providers } from './components/mui/Providers.tsx';
import { Prefetch } from './components/mui/Prefetch.tsx';
import { Splash } from './components/mui/common/Splash.tsx';

const CoreUsers = lazyWithRetry(() => import('./screens/admin/core/Users/Users'));
const Modules = lazyWithRetry(() => import('./screens/admin/core/Modules'));
const Assistants = lazyWithRetry(() => import('./screens/admin/core/Assistants'));
const RoleDetail = lazyWithRetry(() => import('./screens/admin/core/Roles/RoleDetail'));
const Roles = lazyWithRetry(() => import('./screens/admin/core/Roles/Roles'));
const Substitutability = lazyWithRetry(() => import('./screens/admin/core/Substitutability'));
const AdminSubstitutionEdit = lazyWithRetry(() => import('./screens/admin/core/AdminSubstitutionEdit'));
const AdminSubstitutionDetail = lazyWithRetry(() => import('./screens/admin/core/AdminSubstitutionDetail'));
const Settings = lazyWithRetry(() => import('./screens/admin/core/Settings'));
const UserDetail = lazyWithRetry(() => import('./screens/admin/core/Users/UserDetail'));

const ModulesAndAgendas = lazyWithRetry(() => import('./screens/admin/flow/ModulesAndAgendas/ModulesAndAgendas'));
const ModulesAndAgendasDetail = lazyWithRetry(() => import('./screens/admin/flow/ModulesAndAgendas/ModulesAndAgendasDetail'));
const ModulesAndAgendasView = lazyWithRetry(() => import('./screens/admin/flow/ModulesAndAgendas/ModulesAndAgendasView'));
const NotificationTemplates = lazyWithRetry(() => import('./screens/admin/flow/NotificationTemplates/NotificationTemplates'));
const NotificationTemplatesDetail = lazyWithRetry(() => import('./screens/admin/flow/NotificationTemplates/NotificationTemplatesDetail'));
const Tables = lazyWithRetry(() => import('./screens/admin/flow/Tables/Tables'));
const TablesView = lazyWithRetry(() => import('./screens/admin/flow/Tables/TablesView'));
const TablesDetail = lazyWithRetry(() => import('./screens/admin/flow/Tables/TablesDetail'));
const ImportAgendas = lazyWithRetry(() => import('./screens/admin/flow/ImportAgendas/ImportAgendas'));
const ImportAgendaDetail = lazyWithRetry(() => import('./screens/admin/flow/ImportAgendas/ImportAgendaDetail'));
const RestApiConnections = lazyWithRetry(() => import('./screens/admin/flow/RestApiConnections/RestApiConnections'));
const RestApiConnectionDetailScreen = lazyWithRetry(() => import('./screens/admin/flow/RestApiConnections/RestApiConnectionDetailScreen'));

const HeliosDataSources = lazyWithRetry(() => import('./screens/admin/helios/DataSources'));
const HeliosFunctions = lazyWithRetry(() => import('./screens/admin/helios/Functions'));
const HeliosTables = lazyWithRetry(() => import('./screens/admin/helios/Tables'));
const HeliosTemplates = lazyWithRetry(() => import('./screens/admin/helios/Templates/Templates'));
const HeliosTemplatesDetail = lazyWithRetry(() => import('./screens/admin/helios/Templates/TemplatesDetail'));
const HeliosNotificationTemplates = lazyWithRetry(() => import('./screens/admin/helios/NotificationTemplates'));
const HeliosNotificationTemplatesDetail = lazyWithRetry(() => import('./screens/admin/helios/NotificationTemplatesDetail'));
const HeliosImportTemplates = lazyWithRetry(() => import('./screens/admin/helios/ImportTemplates/ImportTemplates'));
const HeliosImportTemplateDetail = lazyWithRetry(() => import('./screens/admin/helios/ImportTemplates/ImportTemplateDetail'));
const HeliosImportTemplateDetails = lazyWithRetry(() => import('./screens/admin/helios/ImportTemplates/ImportTemplateDetails'));

const Overview = lazyWithRetry(() => import('./screens/user/Overview'));
const UserSettings = lazyWithRetry(() => import('./screens/user/UserSettings'));
const UserProfile = lazyWithRetry(() => import('./screens/user/UserProfile').then((module) => ({ default: module.UserProfile })));
const UserModules = lazyWithRetry(() => import('./screens/user/UserModules'));
const UserAgendaNotifications = lazyWithRetry(() => import('./screens/user/UserAgendaNotifications'));
const UserModulesAgendaListScreen = lazyWithRetry(() => import('./screens/user/UserModulesAgendaListScreen'));
const UserHeliosModulesAgendaListScreen = lazyWithRetry(() => import('./screens/user/helios/UserHeliosModulesAgendaListScreen'));
const UserModulesAgendaRecordDetailScreen = lazyWithRetry(() => import('./screens/user/UserModulesAgendaRecordDetailScreen'));
const UserModulesAgendaRecordEditScreen = lazyWithRetry(() => import('./screens/user/UserModulesAgendaRecordEditScreen'));
const UserModulesDetail = lazyWithRetry(() => import('./screens/user/UserModulesDetail/UserModulesDetail'));
const Groups = lazyWithRetry(() => import('./screens/user/contacts/Groups'));
const Contacts = lazyWithRetry(() => import('./screens/user/contacts/Contacts'));
const UserHeliosRecordDetailScreen = lazyWithRetry(() => import('./screens/user/helios/UserHeliosRecordDetailScreen'));
const UserHeliosRecordDetailEditScreen = lazyWithRetry(() => import('./screens/user/helios/UserHeliosRecordDetailEditScreen'));
const Substitutes = lazyWithRetry(() => import('./screens/user/Substitutes'));
const SubstitutionEdit = lazyWithRetry(() => import('./screens/user/SubstitutionEdit'));
const SubstitutionDetail = lazyWithRetry(() => import('./screens/user/SubstitutionDetail'));

const Login = lazyWithRetry(() => import('./screens/Login'));
const MicrosoftRedirect = lazyWithRetry(() => import('./screens/MicrosoftRedirect'));
const GoogleRedirect = lazyWithRetry(() => import('./screens/GoogleRedirect'));
const Download = lazyWithRetry(() => import('./screens/Download'));
const NotFound = lazyWithRetry(() => import('./screens/NotFound'));
const MappingRedirect = lazyWithRetry(() => import('./screens/MappingRedirect'));
const DocumentEditorScreen = lazyWithRetry(() => import('./screens/user/document-editor/DocumentEditorScreen'));

const AdminGuard = () => {
  const { isAdmin } = useLogin();
  const mobileApp = isMobileApp();

  if (mobileApp && window.location.pathname.startsWith('/admin')) {
    return <Navigate to="/user" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/user" replace />;
  }

  return <Outlet />;
};

type AdminSection = keyof ReturnType<typeof getAdminAccessFlags>;

const PermissionGuard = ({ section, children }: { section: AdminSection; children: ReactNode }) => {
  const { permissions } = useLogin();

  return getAdminAccessFlags(permissions)[section] ? <>{children}</> : <Navigate to="/user" replace />;
};

const AppNavigationBridge = () => {
  const navigate = useNavigate();

  useEffect(() => registerAppNavigation(navigate), [navigate]);

  return null;
};

export const Router = () => {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/user/documents/:agendaId/:recordId/:fileId',
          element: (
            <Providers>
              <DocumentEditorScreen />
            </Providers>
          ),
          errorElement: <ErrorPage />,
        },
        {
          path: '/',
          element: (
            <Providers>
              <AppNavigationBridge />
              <Prefetch fallback={<Splash />}>
                <Layout />
              </Prefetch>
            </Providers>
          ),
          errorElement: <ErrorPage />,
          children: [
            {
              index: true,
              element: <Navigate to="/user" replace />,
            },
            {
              path: 'login',
              element: <Login />,
            },
            {
              path: 'microsoftoauth2',
              element: <MicrosoftRedirect />,
            },
            {
              path: 'googleoauth2',
              element: <GoogleRedirect />,
            },
            {
              path: 'google-mapping-redirect',
              element: <MappingRedirect />,
            },
            {
              path: 'microsoft-mapping-redirect',
              element: <MappingRedirect />,
            },
            {
              path: 'admin',
              element: <AdminGuard />,
              children: [
                {
                  path: 'flow',
                  element: (
                    <PermissionGuard section="flow">
                      <Outlet />
                    </PermissionGuard>
                  ),
                  children: [
                    {
                      path: 'modules&agendas',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <ModulesAndAgendas />,
                        },
                        {
                          path: 'create/:id/:tab',
                          element: <ModulesAndAgendasDetail />,
                        },
                        {
                          path: 'edit/:id/:tab/:shownName',
                          element: <Outlet />,
                          children: [
                            {
                              index: true,
                              element: <ModulesAndAgendasDetail />,
                            },
                            {
                              path: ':conditionId',
                              element: <ModulesAndAgendasDetail />,
                            },
                          ],
                        },
                        {
                          path: ':agendaId/view',
                          element: <Outlet />,
                          children: [
                            {
                              path: ':id/:tab/:shownName',
                              element: <ModulesAndAgendasView />,
                            },
                            {
                              path: 'create/:tab',
                              element: <ModulesAndAgendasView />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'tables',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <Tables />,
                        },
                        {
                          path: ':tableId',
                          element: <Outlet />,
                          children: [
                            {
                              path: ':shownName',
                              element: <TablesDetail />,
                            },
                            {
                              path: 'view',
                              element: <Outlet />,
                              children: [
                                {
                                  path: ':id/:tab/:shownName',
                                  element: <TablesView />,
                                },
                                {
                                  path: 'create/:tab',
                                  element: <TablesView />,
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'notification',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <NotificationTemplates />,
                        },
                        {
                          path: ':id',
                          element: <NotificationTemplatesDetail />,
                        },
                      ],
                    },
                    {
                      path: 'agenda-imports',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <ImportAgendas />,
                        },
                        {
                          path: ':importId/:shownName',
                          element: <ImportAgendaDetail />,
                        },
                      ],
                    },
                    {
                      path: 'rest-api-connections',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <RestApiConnections />,
                        },
                        {
                          path: ':connectionId',
                          element: <RestApiConnectionDetailScreen />,
                        },
                        {
                          path: ':connectionId/templates/:templateId',
                          element: <RestApiConnectionDetailScreen />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'helios',
                  element: (
                    <PermissionGuard section="helios">
                      <Outlet />
                    </PermissionGuard>
                  ),
                  children: [
                    {
                      path: 'tables',
                      element: <HeliosTables />,
                    },
                    {
                      path: 'templates',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <HeliosTemplates />,
                        },
                        {
                          path: 'create/:id/:tab',
                          element: <HeliosTemplatesDetail />,
                        },
                        {
                          path: 'edit/:id/:tab/:shownName',
                          element: <Outlet />,
                          children: [
                            {
                              index: true,
                              element: <HeliosTemplatesDetail />,
                            },
                            {
                              path: 'condition/:conditionId',
                              element: <HeliosTemplatesDetail />,
                            },
                            {
                              path: ':conditionType/:conditionId?',
                              element: <HeliosTemplatesDetail />,
                            },
                            {
                              path: 'condition/:conditionId',
                              element: <HeliosTemplatesDetail />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'data-source',
                      element: <HeliosDataSources />,
                    },
                    {
                      path: 'functions',
                      element: <HeliosFunctions />,
                    },
                    {
                      path: 'notification',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <HeliosNotificationTemplates />,
                        },
                        {
                          path: ':id',
                          element: <HeliosNotificationTemplatesDetail />,
                        },
                      ],
                    },
                    {
                      path: 'templates-import',
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          element: <HeliosImportTemplates />,
                        },
                        {
                          path: ':id/:shownName',
                          element: <HeliosImportTemplateDetails />,
                        },
                        {
                          path: ':id/:detailId/:shownName',
                          element: <HeliosImportTemplateDetail />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'core',
                  element: <Outlet />,
                  children: [
                    {
                      path: 'users',
                      element: (
                        <PermissionGuard section="coreUsers">
                          <Outlet />
                        </PermissionGuard>
                      ),
                      children: [
                        {
                          index: true,
                          element: <CoreUsers />,
                        },
                        {
                          path: ':id',
                          element: <UserDetail />,
                        },
                      ],
                    },
                    {
                      path: 'modules',
                      element: (
                        <PermissionGuard section="coreModules">
                          <Modules />
                        </PermissionGuard>
                      ),
                    },
                    {
                      path: 'assistants',
                      element: (
                        <PermissionGuard section="coreAssistants">
                          <Assistants />
                        </PermissionGuard>
                      ),
                    },
                    {
                      path: 'roles',
                      element: (
                        <PermissionGuard section="coreRoles">
                          <Outlet />
                        </PermissionGuard>
                      ),
                      children: [
                        {
                          index: true,
                          element: <Roles />,
                        },
                        {
                          path: ':id',
                          element: <RoleDetail />,
                        },
                      ],
                    },
                    {
                      path: 'substitutability',
                      element: (
                        <PermissionGuard section="coreSubstitutability">
                          <Outlet />
                        </PermissionGuard>
                      ),
                      children: [
                        {
                          index: true,
                          element: <Substitutability />,
                        },
                        {
                          path: ':id',
                          element: <Outlet />,
                          children: [
                            {
                              index: true,
                              element: <AdminSubstitutionEdit />,
                            },
                            {
                              path: 'detail',
                              element: <AdminSubstitutionDetail />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'app-settings',
                      element: (
                        <PermissionGuard section="coreSettings">
                          <Settings />
                        </PermissionGuard>
                      ),
                    },
                  ],
                },
                {
                  path: '*',
                  element: <NotFound />,
                },
              ],
            },
            {
              path: 'user',
              handle: { excludeFromBreadcrumbs: true },
              children: [
                {
                  index: true,
                  element: <Overview />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'overview',
                  element: <Overview />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'settings',
                  element: <UserSettings />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'profile',
                  element: <UserProfile />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'modules',
                  element: <UserModules />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'notifications/info',
                  element: <UserAgendaNotifications />,
                  handle: { hideBreadcrumbs: true },
                },
                {
                  path: 'notifications/action',
                  element: <UserAgendaNotifications />,
                  handle: { hideBreadcrumbs: true, actionRequired: true },
                },
                {
                  path: 'modules/:id',
                  handle: { segmentType: 'module' },
                  element: <Outlet />,
                  children: [
                    {
                      index: true,
                      handle: { excludeFromBreadcrumbs: true },
                      element: <UserModulesDetail />,
                    },
                    {
                      path: 'Flow',
                      element: <Outlet />,
                      handle: { excludeFromBreadcrumbs: true, systemType: 'Flow' as const },
                      children: [
                        {
                          path: ':agendaId',
                          handle: { segmentType: 'agenda' },
                          element: <Outlet />,
                          children: [
                            {
                              index: true,
                              handle: { excludeFromBreadcrumbs: true },
                              element: <UserModulesAgendaListScreen />,
                            },
                            {
                              path: 'view/:tabId',
                              handle: { excludeFromBreadcrumbs: true },
                              element: <UserModulesAgendaListScreen />,
                            },
                            {
                              path: 'r/:recordId',
                              element: <Outlet />,
                              handle: { excludeFromBreadcrumbs: true },
                              children: [
                                {
                                  index: true,
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserModulesAgendaRecordEditScreen />,
                                },
                                {
                                  path: 'detail',
                                  handle: { segmentType: 'record' },
                                  element: <UserModulesAgendaRecordDetailScreen />,
                                },
                                {
                                  path: 'detail/:versionId',
                                  handle: { segmentType: 'record' },
                                  element: <UserModulesAgendaRecordDetailScreen />,
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'Helios',
                      handle: { systemType: 'Helios' as const, excludeFromBreadcrumbs: true },
                      element: <Outlet />,
                      children: [
                        {
                          path: ':agendaId',
                          handle: { segmentType: 'agenda' },
                          element: <Outlet />,
                          children: [
                            {
                              index: true,
                              handle: { excludeFromBreadcrumbs: true },
                              element: <UserHeliosModulesAgendaListScreen />,
                            },
                            {
                              path: 'r/:recordId',
                              handle: { excludeFromBreadcrumbs: true },
                              element: <Outlet />,
                              children: [
                                {
                                  path: 'detail',
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserHeliosRecordDetailScreen />,
                                },
                                {
                                  path: 'detail/edit',
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserHeliosRecordDetailEditScreen />,
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: 'm/:childId',
                      handle: { segmentType: 'childModule' },
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          handle: { excludeFromBreadcrumbs: true },
                          element: <UserModulesDetail />,
                        },
                        {
                          path: 'Flow',
                          element: <Outlet />,
                          handle: { excludeFromBreadcrumbs: true, systemType: 'Flow' as const },
                          children: [
                            {
                              path: ':agendaId',
                              handle: { segmentType: 'agenda' },
                              element: <Outlet />,
                              children: [
                                {
                                  index: true,
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserModulesAgendaListScreen />,
                                },
                                {
                                  path: 'view/:tabId',
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserModulesAgendaListScreen />,
                                },
                                {
                                  path: 'r/:recordId',
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <Outlet />,
                                  children: [
                                    {
                                      index: true,
                                      handle: { excludeFromBreadcrumbs: true },
                                      element: <UserModulesAgendaRecordEditScreen />,
                                    },
                                    {
                                      path: 'detail',
                                      handle: { segmentType: 'record' },
                                      element: <UserModulesAgendaRecordDetailScreen />,
                                    },
                                    {
                                      path: 'detail/:versionId',
                                      handle: { segmentType: 'record' },
                                      element: <UserModulesAgendaRecordDetailScreen />,
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        {
                          path: 'Helios',
                          handle: { systemType: 'Helios' as const, excludeFromBreadcrumbs: true },
                          element: <Outlet />,
                          children: [
                            {
                              path: ':agendaId',
                              handle: { segmentType: 'agenda' },
                              element: <Outlet />,
                              children: [
                                {
                                  index: true,
                                  handle: { excludeFromBreadcrumbs: true },
                                  element: <UserHeliosModulesAgendaListScreen />,
                                },
                                {
                                  path: 'r/:recordId',
                                  element: <Outlet />,
                                  handle: { excludeFromBreadcrumbs: true },
                                  children: [
                                    {
                                      path: 'detail',
                                      handle: { excludeFromBreadcrumbs: true },
                                      element: <UserHeliosRecordDetailScreen />,
                                    },
                                    {
                                      path: 'detail/edit',
                                      handle: { excludeFromBreadcrumbs: true },
                                      element: <UserHeliosRecordDetailEditScreen />,
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'contacts',
                  handle: { hideBreadcrumbs: true },
                  element: <Contacts />,
                },
                {
                  path: 'contacts',
                  handle: { hideBreadcrumbs: true },

                  children: [
                    {
                      index: true,
                      handle: { hideBreadcrumbs: true },
                      element: <Groups />,
                    },
                    {
                      path: 'groups',
                      handle: { hideBreadcrumbs: true },
                      element: <Groups />,
                    },
                  ],
                },
                {
                  path: 'substitutes',
                  handle: { hideBreadcrumbs: true },
                  element: <Outlet />,
                  children: [
                    {
                      index: true,
                      handle: { hideBreadcrumbs: true },
                      element: <Substitutes />,
                    },
                    {
                      path: ':id',
                      handle: { hideBreadcrumbs: true },
                      element: <Outlet />,
                      children: [
                        {
                          index: true,
                          handle: { hideBreadcrumbs: true },
                          element: <SubstitutionEdit />,
                        },
                        {
                          path: 'detail',
                          handle: { hideBreadcrumbs: true },
                          element: <SubstitutionDetail />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'download/:fileUri',
                  handle: { hideBreadcrumbs: true },
                  element: <Download />,
                },
                {
                  path: '*',
                  handle: { hideBreadcrumbs: true },
                  element: <NotFound />,
                },
              ],
            },
            {
              path: '*',
              element: <NotFound />,
            },
          ],
        },
      ]),
    []
  );

  return <RouterProvider router={router} />;
};
